import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  useInvoices,
  type DocumentType,
  type InvoiceRow,
} from "../hooks/useInvoices.js";
import { useCompanies } from "../hooks/useCompanies.js";
import { api } from "../api/rest.js";
import { formatAmount, formatDate, formatDateTime } from "../lib/format.js";
import type { GlobalFiltersValue } from "./GlobalFilters.js";

interface Props {
  companyId: string;
  filters: GlobalFiltersValue;
}

const PAGE_SIZE = 25;

export function InvoicesTable({ companyId, filters }: Props) {
  const [pageIndex, setPageIndex] = useState(0);
  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceRow | null>(null);
  const [invoiceToMove, setInvoiceToMove] = useState<InvoiceRow | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);

  const orderByField = sorting[0]?.id ?? "created_at";
  const orderByDir: "asc" | "desc" = sorting[0]?.desc ? "desc" : "asc";

  const query = useInvoices({
    companyId,
    from: filters.from,
    to: filters.to,
    pageIndex,
    pageSize: PAGE_SIZE,
    orderByField,
    orderByDir,
    supplierIlike: filters.supplier,
    documentTypes: filters.documentTypes,
  });

  const columns = useMemo<ColumnDef<InvoiceRow>[]>(
    () => [
      {
        accessorKey: "document_type",
        header: "Typ",
        enableSorting: false,
        cell: (ctx) => (
          <DocumentTypeBadge type={ctx.getValue<DocumentType>()} uncertain={ctx.row.original.classification_uncertain} />
        ),
      },
      {
        accessorKey: "created_at",
        header: "Přidáno",
        cell: (ctx) => (
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {formatDateTime(ctx.getValue<string>())}
          </span>
        ),
      },
      {
        accessorKey: "issue_date",
        header: "Vystaveno",
        cell: (ctx) => formatDate(ctx.getValue<string | null>()),
      },
      {
        accessorKey: "due_date",
        header: "Splatnost",
        cell: (ctx) => formatDate(ctx.getValue<string | null>()),
      },
      { accessorKey: "supplier_name", header: "Dodavatel" },
      {
        id: "amount",
        header: "Částka",
        cell: (ctx) =>
          formatAmount(
            ctx.row.original.total_amount,
            ctx.row.original.currency,
          ),
      },
      { accessorKey: "currency", header: "Měna" },
      { accessorKey: "variable_symbol", header: "VS" },
      { accessorKey: "invoice_number", header: "Č. faktury" },
      {
        id: "actions",
        header: "Akce",
        enableSorting: false,
        cell: (ctx) => (
          <InvoiceActions
            invoice={ctx.row.original}
            onDelete={() => setInvoiceToDelete(ctx.row.original)}
            onMove={() => setInvoiceToMove(ctx.row.original)}
          />
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: query.data?.rows ?? [],
    columns,
    state: { sorting },
    manualSorting: true,
    manualPagination: true,
    onSortingChange: (updater) => {
      setSorting(typeof updater === "function" ? updater(sorting) : updater);
      setPageIndex(0);
    },
    pageCount: Math.ceil((query.data?.total ?? 0) / PAGE_SIZE),
    getCoreRowModel: getCoreRowModel(),
  });

  const total = query.data?.total ?? 0;
  const totalSum = query.data?.totalSum ?? null;
  const isEmpty = !query.isLoading && total === 0;

  // Detekce měny pro zobrazení součtu — pokud jsou všechny řádky aktuální
  // stránky ve stejné měně, použijeme ji; jinak součet zobrazíme bez měny.
  // (Server-side `sum` agreguje napříč měnami, takže smíšený mix dává jen
  // číslo bez kontextu — proto preferujeme buďto jednu měnu, nebo nic.)
  const currencies = new Set(
    (query.data?.rows ?? [])
      .map((r) => r.currency)
      .filter((c): c is string => !!c),
  );
  const primaryCurrency = currencies.size === 1 ? [...currencies][0]! : null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-slate-200">
        <div className="text-sm text-slate-600">
          {query.isLoading
            ? "Načítám…"
            : (
                <>
                  Celkem <strong>{total}</strong> faktur
                  {totalSum !== null && (
                    <>
                      {" · "}
                      Součet:{" "}
                      <strong>{formatAmount(totalSum, primaryCurrency)}</strong>
                      {!primaryCurrency && currencies.size > 1 && (
                        <span className="text-xs text-slate-400 ml-1">
                          (různé měny)
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
        </div>
        <ZipDownloadButton companyId={companyId} filters={filters} />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    className={`px-3 py-2 select-none ${
                      h.column.getCanSort() ? "cursor-pointer" : ""
                    }`}
                    onClick={
                      h.column.getCanSort()
                        ? h.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    {h.column.getIsSorted() === "asc" && " ▲"}
                    {h.column.getIsSorted() === "desc" && " ▼"}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {isEmpty && (
              <tr>
                <td colSpan={columns.length} className="text-center py-6 text-slate-400">
                  Žádné faktury podle aktuálního filtru.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3 p-3 border-t border-slate-200 text-sm">
        <button
          className="px-2 py-1 border rounded disabled:opacity-50"
          onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
          disabled={pageIndex === 0}
        >
          ‹ Předchozí
        </button>
        <span>
          Stránka {pageIndex + 1} / {Math.max(1, table.getPageCount())}
        </span>
        <button
          className="px-2 py-1 border rounded disabled:opacity-50"
          onClick={() =>
            setPageIndex((p) => Math.min(table.getPageCount() - 1, p + 1))
          }
          disabled={pageIndex + 1 >= table.getPageCount()}
        >
          Další ›
        </button>
      </div>
      {invoiceToDelete && (
        <DeleteInvoiceDialog
          invoice={invoiceToDelete}
          onClose={() => setInvoiceToDelete(null)}
        />
      )}
      {invoiceToMove && (
        <MoveInvoiceDialog
          invoice={invoiceToMove}
          currentCompanyId={companyId}
          onClose={() => setInvoiceToMove(null)}
        />
      )}
    </div>
  );
}

function InvoiceActions({
  invoice,
  onDelete,
  onMove,
}: {
  invoice: InvoiceRow;
  onDelete: () => void;
  onMove: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <DownloadButton invoiceId={invoice.id} />
      <button
        type="button"
        onClick={onMove}
        title="Přesunout na jinou firmu"
        aria-label="Přesunout na jinou firmu"
        className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      >
        <MoveIcon />
      </button>
      <button
        type="button"
        onClick={onDelete}
        title="Smazat fakturu"
        aria-label="Smazat fakturu"
        className="inline-flex h-8 w-8 items-center justify-center rounded border border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
      >
        <TrashIcon />
      </button>
    </div>
  );
}

function MoveInvoiceDialog({
  invoice,
  currentCompanyId,
  onClose,
}: {
  invoice: InvoiceRow;
  currentCompanyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const companies = useCompanies();
  const [selected, setSelected] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const otherCompanies = (companies.data ?? []).filter(
    (c) => c.id !== currentCompanyId,
  );

  const handleMove = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.changeInvoiceCompany(invoice.id, selected);
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  const label =
    invoice.invoice_number ?? invoice.original_filename ?? "vybranou fakturu";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">Přesunout fakturu</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-slate-500 hover:text-slate-900 disabled:opacity-50"
            aria-label="Zavřít"
          >
            ✕
          </button>
        </div>

        <p className="mt-2 text-sm text-slate-600">
          Faktura <span className="font-medium">{label}</span> bude přiřazena
          k nově vybrané firmě a zmizí ze seznamu aktuální firmy.
        </p>

        <label className="mt-4 block">
          <span className="text-sm text-slate-600">Cílová firma</span>
          <select
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">— vyber firmu —</option>
            {otherCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded border border-slate-300 disabled:opacity-50"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={handleMove}
            disabled={submitting || !selected}
            className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Přesouvám…" : "Přesunout"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DownloadButton({ invoiceId }: { invoiceId: string }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const { url } = await api.getDownloadUrl(invoiceId);
      window.open(url, "_blank", "noopener");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      title="Stáhnout originál"
      aria-label="Stáhnout originál"
      className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
    >
      <DownloadIcon />
    </button>
  );
}

function DeleteInvoiceDialog({
  invoice,
  onClose,
}: {
  invoice: InvoiceRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label =
    invoice.invoice_number ?? invoice.original_filename ?? "vybranou fakturu";

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await api.deleteInvoice(invoice.id);
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Smazat fakturu?</h2>
            <p className="mt-2 text-sm text-slate-600">
              Faktura {label} se skryje ze seznamu a nebude zahrnutá do ZIP
              exportu.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="text-slate-500 hover:text-slate-900 disabled:opacity-50"
            aria-label="Zavřít"
          >
            ✕
          </button>
        </div>

        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-3 py-1.5 rounded border border-slate-300 disabled:opacity-50"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? "Mažu…" : "Smazat"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Barevný badge podle typu dokumentu — `invoice` je default tichý šedý,
 * ostatní typy se vizuálně odliší, ať uživatel vidí v tabulce na první pohled,
 * že to není běžná faktura. */
function DocumentTypeBadge({ type, uncertain = false }: { type: DocumentType; uncertain?: boolean }) {
  const cfg: Record<
    DocumentType,
    { label: string; className: string; title: string }
  > = {
    invoice: {
      label: "Faktura",
      className: "bg-slate-100 text-slate-700 border-slate-200",
      title: "Standardní daňový doklad",
    },
    proforma: {
      label: "Proforma",
      className: "bg-amber-50 text-amber-800 border-amber-200",
      title: "Proforma / pro forma faktura — zálohová, nezakládá nárok na odpočet DPH",
    },
    payment_request: {
      label: "Výzva k platbě",
      className: "bg-orange-50 text-orange-800 border-orange-200",
      title: "Výzva k platbě / upomínka",
    },
    set_off: {
      label: "Zápočet",
      className: "bg-violet-50 text-violet-800 border-violet-200",
      title: "Jednostranný / vzájemný zápočet pohledávek a závazků",
    },
    credit_note: { label: "Dobropis", title: "Dobropis", className: "bg-slate-100 text-slate-700 border-slate-200" },
    delivery_note: { label: "Dodací / záruční list", title: "Dodací / záruční list", className: "bg-slate-100 text-slate-700 border-slate-200" },
    receivables_confirmation: { label: "Odsouhlasení pohledávek", title: "Odsouhlasení pohledávek", className: "bg-slate-100 text-slate-700 border-slate-200" },
    order: { label: "Objednávka", title: "Objednávka", className: "bg-slate-100 text-slate-700 border-slate-200" },
    other: {
      label: "Jiné",
      className: "bg-slate-100 text-slate-500 border-slate-200",
      title: "Jiný dokument (potvrzení o platbě, smlouva, …)",
    },
  };
  const c = cfg[type] ?? cfg.other;
  return (
    <span
      title={uncertain ? "Nejisté rozpoznání i po kontrole — zkontrolujte dokument" : c.title}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium ${uncertain ? "bg-red-50 text-red-700 border-red-300" : c.className}`}
    >
      {c.label}
    </span>
  );
}

function DownloadIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  );
}

function MoveIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 9l-3 3 3 3" />
      <path d="M9 5l3 -3 3 3" />
      <path d="M15 19l3 -3 -3 -3" />
      <path d="M19 9l3 3 -3 3" />
      <path d="M2 12h20" />
      <path d="M12 2v20" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function ZipDownloadButton({
  companyId,
  filters,
}: {
  companyId: string;
  filters: GlobalFiltersValue;
}) {
  const handleClick = async () => {
    const res = await fetch(api.zipUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        companyId,
        fromIssueDate: filters.from,
        toIssueDate: filters.to,
      }),
    });
    if (!res.ok) {
      alert(`ZIP request failed: ${res.status}`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices-${filters.from}_${filters.to}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button
      onClick={handleClick}
      className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
    >
      Stáhnout ZIP
    </button>
  );
}
